import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const Pagination = React.memo(({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const renderPageNumbers = () => {
        const pages = [];
        // Responsive max visible pages
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
        const maxVisible = isMobile ? 3 : 5;
        
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    aria-label={`Go to page ${i}`}
                    aria-current={currentPage === i ? 'page' : undefined}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl font-black text-[10px] sm:text-xs transition-all border ${
                        currentPage === i
                            ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20'
                            : 'bg-white text-slate-400 border-slate-100 hover:border-primary/20 hover:text-primary hover:bg-slate-50'
                    }`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    return (
        <div className="flex items-center justify-center gap-2 mt-12 py-8">
            {/* First Page */}
            <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                aria-label="Go to first page"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white text-slate-400 border border-slate-100 hover:border-primary/20 hover:text-primary transition-all flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed group"
            >
                <ChevronsLeft size={14} className="sm:w-4 sm:h-4" />
            </button>

            {/* Prev */}
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Go to previous page"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white text-slate-400 border border-slate-100 hover:border-primary/20 hover:text-primary transition-all flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            >
                <ChevronLeft size={14} className="sm:w-4 sm:h-4" />
            </button>

            {/* Numbers */}
            <div className="flex items-center gap-1 sm:gap-2 px-1 sm:px-4">
                {renderPageNumbers()}
            </div>

            {/* Next */}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Go to next page"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white text-slate-400 border border-slate-100 hover:border-primary/20 hover:text-primary transition-all flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            >
                <ChevronRight size={14} className="sm:w-4 sm:h-4" />
            </button>

            {/* Last Page */}
            <button
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
                aria-label="Go to last page"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white text-slate-400 border border-slate-100 hover:border-primary/20 hover:text-primary transition-all flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            >
                <ChevronsRight size={14} className="sm:w-4 sm:h-4" />
            </button>
        </div>
    );
});

export default Pagination;
